import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import baseConfig from '@/config';
import { isCnArea } from '@/utils/envHelper';
import { ProductAvatarBackgroundType } from '@/utils/eventlog/eventNewConfig';

// 存储持久化的key
const key = 'f_product_avatar_state';

export type AvatarInfoType = {
  url: string;
  previewUrl: string;
  title: string;
  isUpload?: boolean;
};

export enum ScriptType {
  TTS = 0,
  AUDIO = 1,
}

// 音色信息类型
export type VoiceInfoType = {
  icon: string;
  tags?: string[];
  previewUrl?: string;
  title: string;
  templateId?: string | number;
  id?: string | number;
  voiceId: string;
  gender: number;
  isVcm: boolean;
  isPro?: boolean;
  [propname: string]: any;
};

// 上传音频信息类型
export type UploadVoiceInfo = {
  url: string;
  name: string;
};

export type UploadProductInfo = {
  url: string;
  previewUrl: string;
  name: string;
};

export interface IMergeImages {
  url?: string;
  taskId?: number;
  count: number;
  status: 'loading' | 'success' | 'fail' | 'timeout';
}

// Product Avatar Store
interface IProductAvatarStore {
  avatarInfo: AvatarInfoType | null;
  setAvatarInfo: (avatarInfo: AvatarInfoType | null) => void;
  // tts音频
  voiceUrl: string;
  setVoiceUrl: (url: string) => void;
  ttsSpeed: number;
  setTtsSpeed: (speed: number) => void;
  uploadVoiceInfo: UploadVoiceInfo | null;
  setUploadVoiceInfo: (info: UploadVoiceInfo | null) => void;
  ttsDuration: number;
  setTtsDuration: (ttsDuration: number) => void;
  duration: number;
  setDuration: (time: number) => void;
  scriptType: ScriptType;
  setScriptType: (scriptType: ScriptType) => void;
  script: string;
  setScript: (script: string) => void;
  voiceInfo: VoiceInfoType | null;
  setVoiceInfo: (info: VoiceInfoType | null) => void;
  productInfo: UploadProductInfo | null;
  setProductInfo: (url: UploadProductInfo | null) => void;
  ttsGenerating: boolean;
  setTtsGenerating: (generating: boolean) => void;
  // 合成的图片
  mergeImages: IMergeImages[];
  setMergeImages: (mergeImages: IMergeImages[]) => void;

  // 选中的合成图片
  selectMergeImage: string;
  setSelectMergeImage: (selectMergeImage: string) => void;
  resetData: () => void;
  // 字体模板
  fontTemplateInfo: {
    id: string;
    isPro: boolean;
  };
  setFontTemplateInfo: (value: { id: string; isPro: boolean }) => void;
  // 背景类型
  backgroundType: ProductAvatarBackgroundType;
  setBackgroundType: (type: ProductAvatarBackgroundType) => void;
  // 自定义背景图片
  customBackgroundImage: string;
  setCustomBackgroundImage: (customBackgroundImage: string) => void;

  ratio: string;
  setRatio: (ratio: string) => void;

  // 要生成字幕文件的event返回数据
  extraEventResult: string;
  setExtraEventResult: (value: string) => void;
}

const initData = {
  // 选择的数字人信息
  avatarInfo: null,
  // 产品图信息
  productInfo: null,
  // 生成的TTS音频链接
  voiceUrl: '',
  // TTS音频速度
  ttsSpeed: 1,
  // 上传的音频文件信息
  uploadVoiceInfo: null,
  // 生成的TTS音频时长
  ttsDuration: 0,
  // 上传的音频时长
  duration: 0,
  // 脚本面板类型：0-TTS，1-音频文件
  scriptType: ScriptType.TTS,
  // 脚本内容
  script: '',
  ttsGenerating: false,
  mergeImages: [],
  selectMergeImage: '',
  // 选择的音色信息
  voiceInfo: {
    icon: '/images/voice/default_man_icon.png',
    previewUrl: 'https://assets.falcocut.ai/template/default_voiceInfo_man.mp3',
    title: isCnArea ? 'Christopher-美式英语' : 'Christopher-AmE',
    isPro: false,
    tags: isCnArea ? ['男', '年轻', '平静的'] : ['Male', 'Young Adult', 'Calm'],
    voiceId: 'Liam',
    gender: 1,
    tabGroupName: 'default',
    isVcm: true,
  },
  // 字体
  fontTemplateInfo: { id: '', isPro: false },

  backgroundType: ProductAvatarBackgroundType.AI,
  customBackgroundImage: '',
  ratio: 'auto',
  extraEventResult: '',
};

export const useProductAvatarStore = createWithEqualityFn<IProductAvatarStore>()(
  devtools(
    (set) => ({
      ...initData,

      setAvatarInfo(avatarInfo) {
        set({ avatarInfo });
      },

      setProductInfo(productInfo) {
        set({ productInfo });
      },

      setVoiceUrl(voiceUrl) {
        set({ voiceUrl });
      },

      setTtsSpeed(speed: number) {
        set({ ttsSpeed: speed });
      },

      setUploadVoiceInfo(uploadVoiceInfo) {
        set({ uploadVoiceInfo });
      },

      setTtsDuration: (ttsDuration: number) => {
        set({ ttsDuration });
      },

      setDuration(duration) {
        set({ duration });
      },

      setScriptType(scriptType) {
        set({ scriptType });
      },

      setScript(script) {
        set({ script });
      },

      setVoiceInfo(voiceInfo) {
        set({ voiceInfo });
      },

      setTtsGenerating(ttsGenerating) {
        set({ ttsGenerating });
      },

      setMergeImages: (mergeImages) => set(() => ({ mergeImages })),

      setSelectMergeImage: (selectMergeImage) => set(() => ({ selectMergeImage })),

      setFontTemplateInfo: (fontTemplateInfo) => set({ fontTemplateInfo }),
      setBackgroundType: (backgroundType) => set({ backgroundType }),
      setCustomBackgroundImage: (customBackgroundImage) => set({ customBackgroundImage }),

      setRatio: (ratio) => set({ ratio }),

      setExtraEventResult: (extraEventResult) => set({ extraEventResult }),

      resetData: () => {
        set({
          ...initData,
        });
      },
    }),
    {
      name: key,
      enabled: baseConfig.debuggable,
    },
  ),

  shallow,
);
